import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'
import {toast} from 'react-toastify'
import {toastActionableConfig, toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {useCognitoAuth} from '../auth'
import {getPins, PINS_UNAVAILABLE, pinsErrorMessage, PinsResponse, PinsState, putPins} from './api'
import {FolderMove, isPinned, MAX_PINS, movePins, Pin, samePins, withoutPins, withPin} from './pins'

/** React Query keys for the pins, per signed-in user. */
export const pinsQueryKeys = {
  all: ['pins'] as const,
  forUser: (sub: string) => ['pins', sub] as const,
}

/** Every change of the list, from any component: see the success handler below. */
export const PINS_MUTATION_KEY = ['pins', 'update'] as const

/** The list changes rarely and from one person; the desktop may change it too, so not forever. */
const PINS_STALE_MS = 5 * 60 * 1000

const NO_PINS: Pin[] = []

/** The signed-in user's pins (`GET /v1/me/pins`). Empty and unavailable while unknown or on error. */
export function usePins(): {available: boolean; pins: Pin[]; isLoading: boolean} {
  const {currentUser} = useCognitoAuth()
  const sub = currentUser?.sub ?? ''
  const query = useQuery(pinsQueryKeys.forUser(sub), getPins, {
    enabled: !!sub,
    staleTime: PINS_STALE_MS,
    retry: false,
  })
  return {
    available: query.data?.available === true,
    pins: query.data?.available ? query.data.pins : NO_PINS,
    isLoading: query.isInitialLoading,
  }
}

export interface PinActions {
  /** Whether pinning works for this user: the list was read. Pin actions are hidden otherwise. */
  available: boolean
  pins: Pin[]
  /** Whether any of `candidates` is pinned (a folder can be pinned by path or, if shared, by share). */
  isPinned: (...candidates: Pin[]) => boolean
  /** Adds the pin at the end of the list. */
  pin: (pin: Pin) => void
  /** Removes every one of these pins. */
  unpin: (...pins: Pin[]) => void
  /**
   * Pins follow folders the user moved or renamed: in My Files, or inside the share `shareId`. A
   * shared folder's own pin never needs to (it names the share).
   */
  followMoves: (moves: FolderMove[], shareId?: string) => void
}

interface PinsChange {
  /** The whole list to send. */
  next: Pin[]
  /** What was on screen before, to go back to if the server refuses. */
  previous: PinsState
}

/**
 * Pins and unpins, with optimistic updates: the list changes on screen at once and goes back if the
 * server refuses. Every change sends the whole list (`PUT` replaces it), computed from the list on
 * screen at that moment, so quick changes in a row build on each other.
 */
export function usePinActions(): PinActions {
  const queryClient = useQueryClient()
  const {currentUser} = useCognitoAuth()
  const sub = currentUser?.sub ?? ''
  const queryKey = useMemo(() => pinsQueryKeys.forUser(sub), [sub])
  const {available, pins} = usePins()

  const {mutate} = useMutation<PinsResponse, unknown, PinsChange>(({next}) => putPins(next), {
    mutationKey: PINS_MUTATION_KEY,
    onSuccess: (response) => {
      // A later change may be on its way already: its list stays on screen until it answers too.
      // (The mutation in hand still counts as running while this runs.)
      if (queryClient.isMutating({mutationKey: PINS_MUTATION_KEY}) <= 1) {
        queryClient.setQueryData<PinsState>(queryKey, {available: true, ...response})
      }
    },
    onError: (error, {previous}) => {
      getLogger().error('Could not update the pinned folders:', error)
      queryClient.setQueryData<PinsState>(queryKey, previous)
      // What the server has now, in case changes overlapped.
      void queryClient.invalidateQueries(queryKey)
      toast.error(
        `Couldn't update the pinned folders: ${pinsErrorMessage(error)}`,
        toastActionableConfig
      )
    },
  })

  /**
   * Applies `change` to the list on screen, shows the result at once, and sends it. The optimistic
   * update happens here rather than in `onMutate` (which runs a tick later), so a second change made
   * right after the first is computed from the first one's list.
   */
  const update = useCallback(
    (change: (current: Pin[]) => Pin[]) => {
      const previous = queryClient.getQueryData<PinsState>(queryKey) ?? PINS_UNAVAILABLE
      if (!previous.available) {
        return
      }
      const next = change(previous.pins)
      if (samePins(next, previous.pins)) {
        return
      }
      if (next.length > MAX_PINS && next.length > previous.pins.length) {
        toast.warning(
          `You can pin up to ${MAX_PINS} folders. Unpin one to make room.`,
          toastDefaultConfig
        )
        return
      }
      // A read in flight would otherwise land on top of the new list. Cancelling reverts it at once.
      void queryClient.cancelQueries(queryKey)
      queryClient.setQueryData<PinsState>(queryKey, {...previous, pins: next})
      mutate({next, previous})
    },
    [queryClient, queryKey, mutate]
  )

  const pin = useCallback((target: Pin) => update((current) => withPin(current, target)), [update])
  const unpin = useCallback(
    (...targets: Pin[]) => update((current) => withoutPins(current, targets)),
    [update]
  )
  const followMoves = useCallback(
    (moves: FolderMove[], shareId?: string) => {
      if (moves.length > 0) {
        update((current) => movePins(current, moves, shareId))
      }
    },
    [update]
  )
  const pinned = useCallback(
    (...candidates: Pin[]) => candidates.some((candidate) => isPinned(pins, candidate)),
    [pins]
  )

  return {available, pins, isPinned: pinned, pin, unpin, followMoves}
}
